import {LoadingOverlay, Button} from '@finpay-development/shared-components';
import {
  Grid,
  Paper,
} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {RootState} from '../../../../shared/state/root-reducer';
import {AppDispatch} from '../../../../shared/state/store';
import {getPatientDemographics} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {
  loadSelectedVob,
} from '@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk';
import {loadSelectedEstimate} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import '../../../estimate-view.scss';
import _ from 'lodash';
import moment from 'moment';
import {
  CrossoverSelectionEnum,
} from '@finpay-estimation/estimation-types';
import {HeaderSection} from './header-section';
import {BenefitDetailsSection} from './benefit-details-section';
import {PfrDetailsSection} from './pfr-details-section';
import {AttestationSection} from './attestation-section';
import {SectionSelectionModal} from './section-selection-modal';
import type {SectionKey} from './section-selection-modal';
import {
  CarePlanDetailsSectionOld,
} from './care-plan-details-section';

export const sectionPaperStyling = {
  borderRadius: '8px',
  border: '1px solid rgba(0,0,0,0.12)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
};

export const IOCEstimateDetailsV4 = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {eid} = useParams<{ eid?: string }>();

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Record<SectionKey, boolean>>(
      {
        header: true,
        benefitDetails: true,
        carePlanDetails: true,
        pfrDetails: true,
        attestation: true,
      });

  const stateFields = {
    isLoadingState: useSelector(
        (state: RootState) => state.adminContext.adminUserContext.isLoading,
    ),
    allClientsMap: useSelector(
        (state: RootState) =>
            state.implementationContext.implementationSpecialistClient.allClientsMap,
    ),
    userProfile: useSelector(
        (state: RootState) => state.userContext.userProfile,
    ),
    isClientLoading: useSelector(
        (state: RootState) =>
            state.implementationContext.implementationSpecialistClient.isLoading,
    ),
    selectedPatient: useSelector(
        (state: RootState) => state.patientContext.selectedPatient,
    ),
    isLoadingPatient: useSelector(
        (state: RootState) => state.patientContext.isLoading.patientDemographics,
    ),
    payersMap: useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext.payersMap,
    ),
    isLoadingPayers: useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext.isLoading.isLoadingPayer,
    ),
    selectedVob: useSelector(
        (state: RootState) => state.vobContext.selectedVob,
    ),
    isLoadingVob: useSelector(
        (state: RootState) => state.vobContext.isLoading.selectedVob,
    ),
    estimate: useSelector((state: RootState) => {
      return state?.estimatorContext.selectedEstimate;
    }),
  };

  const {
    isLoadingState,
    allClientsMap,
    userProfile,
    isClientLoading,
    selectedPatient,
    isLoadingPatient,
    payersMap,
    isLoadingPayers,
    selectedVob,
    isLoadingVob,
    estimate,
  } = stateFields;

  // const roleAccess = checkViewOnlyPermissions(
  //     userProfile.userRole.userRolePages,
  //     RolePageNames.AdmissionsAdvisor,
  //     'VOB',
  //     false,
  // );

  useEffect(() => {

    const fetchData = async () => {

      const estimateResponse = await dispatch(loadSelectedEstimate({
        paramId: -2,
        estimationId: eid!,
      })).unwrap();
      //wrap following in promise.all and add error handling if those api calls fail also add message

      const promiseArr: any[] = [];

      if (estimateResponse?.vobId) {
        promiseArr.push(dispatch(loadSelectedVob({
          vobId: estimateResponse?.vobId,
        })));
      }
      if (estimateResponse?.patientId) {
        promiseArr.push(
            dispatch(getPatientDemographics(estimateResponse.patientId)));
      }

      await Promise.all(promiseArr);

    };

    if ((!estimate?.estimateId && eid) ||
        (estimate?.estimateId != eid && eid)) {

      try {
        setIsDataLoading(true);
        fetchData();
      } catch (e) {

      } finally {
        setIsDataLoading(false);
      }

    }

  }, [eid]);

  const estimateViewSections = useMemo(
      (): Record<SectionKey, React.ReactNode> => {

        const client = estimate?.clientId ?
            allClientsMap.get(+estimate.clientId) :
            null;
        const facility = client ? client.clientFacilities?.find((facility) => {
          return facility.facilityId === estimate?.facilityId;
        }) : null;

        const selectedPayer = selectedVob?.payor?.payorId ?
            payersMap.get(selectedVob?.payor?.payorId) :
            null;
        const selectedPlan = selectedPayer && selectedVob?.plan?.payorPlanId ?
            selectedPayer?.payorPlans.find(
                (plan) => plan.payorPlanId === selectedVob?.plan?.payorPlanId) :
            null;
        // console.log('vob check', selectedVob);
        // console.log('estimate check', estimate);
        // console.log('payer check', selectedPayer);
        // console.log('plan check', selectedPlan);

        const isInNetwork = estimate?.rateAsPayorPlan?.payor?.inNetwork;

        const deductibleObj = typeof isInNetwork === 'boolean' ?
            isInNetwork ?
                selectedVob?.innDeductible :
                selectedVob?.oonDeductible :
            null;
        const oopMaxObj = typeof isInNetwork === 'boolean' ?
            isInNetwork ? selectedVob?.innOopm : selectedVob?.oonOopm :
            null;
        const vobNotes = Array.isArray(selectedVob?.notes) ?
            selectedVob?.notes.reduce((acc, curr, index) => {
              return acc + (index === 0 ? '' : ' ') + curr.noteText;
            }, '') :
            '';

        const coveredServices = [];
        const uncoveredServices = [];

        estimate?.facilityLevelOfCare?.forEach((loc) => {
          if (loc.isCovered) {
            coveredServices.push({
              levelOfCareName: loc.facilityLevelOfCareName,
              code: loc.facilityLevelOfCareCode,
              // coveredAmt:
              sessions: loc.losDays,
            });
          }
        });

        const isWithCrossover = estimate?.crossoverSelection ===
            CrossoverSelectionEnum.WITH;

        // console.log('crossover selection', estimate?.crossoverSelection);

        return ({
          header: (
              <HeaderSection
                  title={'Patient Financial Responsibility (PFR) Full Details'}
                  subtitle={client?.clientName || ''}
                  estimateId={estimate?.estimateId ?
                      `${estimate?.estimateId}` :
                      ''}
                  lastUpdateDt={estimate?.lastUpdateDt ?
                      new Date(estimate?.lastUpdateDt).toLocaleDateString(
                          'en-US') :
                      ''}
                  patientDOB={selectedPatient &&
                  selectedPatient.contact?.birthDate
                      ?
                      moment(selectedPatient.contact.birthDate).
                          format('M/D/YYYY')
                      :
                      ''}
                  patientName={`${_.capitalize(
                      selectedPatient.contact?.firstName || '')} ${
                      _.capitalize(selectedPatient.contact?.lastName || '')
                  }`.trim()}
              />
          ),
          benefitDetails: (
              <BenefitDetailsSection
                  payorAndPlanName={`${selectedPayer?.payorName ||
                  ''} / ${selectedPlan?.planName || ''} (${isInNetwork ?
                      'In Network' :
                      'Out of Network'})`}
                  deductible={deductibleObj?.total}
                  deductibleRemaining={deductibleObj?.remaining}
                  oopMax={oopMaxObj?.total}
                  oopMaxRemaining={oopMaxObj?.remaining}
                  isDeductibleIncludedInOopMax={oopMaxObj?.isDeductibleIncluded}
                  notes={vobNotes}
              />
          ),
          carePlanDetails: (
              <CarePlanDetailsSectionOld
                estimate={{
                  facilityLevelOfCare: estimate?.facilityLevelOfCare,
                  isPlanYearCrossover: estimate?.isPlanYearCrossover!,
                  crossoverSelection: estimate?.crossoverSelection!,
                  summary: {
                    coveredCharges: estimate?.summary?.coveredCharges!,
                    unCoveredCharges: estimate?.summary?.unCoveredCharges!,
                    lengthOfStay: estimate?.summary?.lengthOfStay!
                  }
                }}
                facilityName ={facility?.facilityName!}
                admissionDate = {
                estimate?.anticipatedAdmitDate ?
                    moment(estimate?.anticipatedAdmitDate).format('M/D/YYYY') : ""
              }
              />
          ),
          pfrDetails: (
              <PfrDetailsSection
                  doBenefitsReset={!!(selectedVob?.isCopayReset &&
                      isWithCrossover)}
                  isPfrAdjustedForPriorCare={typeof estimate?.summary?.priorCareTotalAdjustedCharges ===
                      'number' && estimate?.summary?.priorCareTotalAdjustedCharges >
                      0}
                  estimatedDeductible={estimate?.summary?.deductibleSpent}
                  estimatedCopay={estimate?.summary?.patientTotalCoPays}
                  estimatedCoinsurance={estimate?.summary?.patientTotalCoInsurance}
                  oopMaxRemaining={estimate?.summary?.remainingOopMaxFromVob}
                  pfrForCoveredServices={estimate?.summary?.coveredSvcsPfr}
                  pfrForUncoveredServices={estimate?.summary?.unCoveredCharges}
                  admissionAssessment={estimate?.summary?.totalSurcharges}
                  estimatedPFR={estimate?.summary?.totalPFR}
                  isWithCrossover={isWithCrossover}
              />
          ),
          attestation: (
              <AttestationSection/>
          )
        });
      },
      [
        estimate,
        selectedPatient,
        allClientsMap,
        payersMap,
        selectedVob,
      ],
  );

  return (
      <Grid>
        {isDataLoading || isClientLoading || isLoadingPayers ?
            <LoadingOverlay/> :
            <main className="patient-details">
              <Paper
                  className="content extra-padding"
                  sx={{
                    marginTop: '1rem',
                    marginBottom: '4rem !important',
                    padding: '24px',
                  }}
                  test-id="estimate-details-container-v4"
              >
                <div className="print-section">
                  {
                    (Object.entries(visibleSections) as any[])
                    .filter(([_, isVisible])=>{
                      return isVisible
                    })
                    .map(([section, _]) => {
                      return (
                          <React.Fragment  key={section}>
                            {estimateViewSections?.[(section) as SectionKey]}
                          </React.Fragment>
                      )
                    })
                  }
                </div>
                <Grid
                    container
                    justifyContent="flex-end"
                    mt={4}
                    spacing={2}
                >
                  <Grid
                      item
                      textAlign="right"
                      mb={3}
                  >
                    <Button onClick={() => {
                      setIsModalOpen(true);
                    }}>
                      Select Sections
                    </Button>
                  </Grid>
                  <Grid item
                  >
                    <Button
                        test-id="download-estimate-v4"
                        onClick={() => {
                          window.print();
                        }}
                    >
                      Print / Save as PDF
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </main>
        }
        {
            isModalOpen && (
                <SectionSelectionModal
                    setIsModalOpen={setIsModalOpen}
                    initialVisibleSections={visibleSections}
                    onUpdateVisibleSections={(next) => setVisibleSections(next)}
                />)
        }
        {/*<Typography className="access-error" test-id="access-error">*/}
        {/*            <span className="message">*/}
        {/*                <LockIcon*/}
        {/*                    style={{paddingRight: '8px', fontSize: '1.6em'}}*/}
        {/*                />*/}
        {/*              {estimateErrorMessage.authenticationError}*/}
        {/*            </span>*/}
        {/*</Typography>*/}
      </Grid>
  );
};


